import {
  MeetingBlockLike,
  MinimalClassTime,
  ScheduleBreakLike,
  ScheduleLike,
  SortedDaysOfWeek,
  DayOfWeekLabelMap,
  ClassTimeType,
} from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';

import { formatPlainDate, formatPlainTime, MultiMap } from '@airreading/common';

export function formatScheduleDates({ startPlainDate, endPlainDate }: ScheduleLike): string {
  return `${formatPlainDate(startPlainDate)} - ${formatPlainDate(endPlainDate)}`;
}

export function formatScheduleBreakInput({ startPlainDate, endPlainDate }: ScheduleBreakLike): string {
  return `${formatPlainDate(startPlainDate)} - ${formatPlainDate(endPlainDate)}`;
}

export function formatScheduleBreak({ startPlainDate, endPlainDate }: ScheduleBreakLike): string {
  const isSingleDay = startPlainDate.equals(endPlainDate);
  const label = isSingleDay
    ? formatPlainDate(startPlainDate)
    : `${formatPlainDate(startPlainDate)} - ${formatPlainDate(endPlainDate)}`;
  return label;
}

export function keyOfMeetingBlock({ day, startPlainTime, endPlainTime }: MeetingBlockLike): string {
  return `${day}-${startPlainTime}-${endPlainTime}`;
}

export function formatMeetingBlockTimes({ startPlainTime, endPlainTime }: MeetingBlockLike): string {
  return `${formatPlainTime(startPlainTime, undefined, 'en-GB')} - ${formatPlainTime(endPlainTime, undefined, 'en-GB')}`;
}

export function formatMeetingBlockInput(meetingBlock: MeetingBlockLike): string {
  const label = formatMeetingBlockTimes(meetingBlock);
  return `${DayOfWeekLabelMap[meetingBlock.day].full}, ${label}`;
}

export function formatMeetingBlockDisplay(meetingBlock: MeetingBlockLike): string {
  const label = `${formatPlainTime(meetingBlock.startPlainTime)} - ${formatPlainTime(meetingBlock.endPlainTime)}`;
  return `${DayOfWeekLabelMap[meetingBlock.day].full}, ${label}`;
}

export function formatMeetingBlock(meetingBlock: MeetingBlockLike): string {
  const label = formatMeetingBlockTimes(meetingBlock);
  return `${DayOfWeekLabelMap[meetingBlock.day].full} @ ${label}`;
}

export function formatStudentClassTimeAbbr(classTime: MinimalClassTime): string {
  const { meetingBlocks, type } = classTime;
  const blocksByClasstime = MultiMap.groupBy(meetingBlocks, (block) => `${block.startPlainTime}-${block.endPlainTime}`);
  const blocksSortedByDayOfWeek = [...blocksByClasstime.values()].toSorted((a, b) => {
    const groupAMeetingDays = a.map(({ day }) => SortedDaysOfWeek.indexOf(day));
    const groupBMeetingDays = b.map(({ day }) => SortedDaysOfWeek.indexOf(day));
    return Math.min(...groupAMeetingDays) - Math.min(...groupBMeetingDays);
  });
  const isOneTimeClassTime = type === ClassTimeType.one_time;

  return blocksSortedByDayOfWeek
    .map((blocks) => {
      // all blocks share the same meeting time
      const days = blocks.map(({ day }) => DayOfWeekLabelMap[day].abbr).join(', ');
      const time = `${formatPlainTime(blocks[0].startPlainTime)} - ${formatPlainTime(blocks[0].endPlainTime)}`;
      return isOneTimeClassTime ? `${time}` : `${days}, ${time}`;
    })
    .join(' | ');
}

/**
 * Convert a PlainTime into an input type=time string value. Input value is always 24-hour format hh:mm
 * @param time PlainTime
 * @returns 'hh:mm'
 */
export function toInputTimeValue(time: Temporal.PlainTime, locale = 'en-GB'): string {
  return time.toLocaleString(locale, { timeStyle: 'short', hourCycle: 'h23' });
}
